import type { ResponseList, ResponseObject } from "~/types/misc";
import type { User } from "~/types/entities";
import { http } from "~/lib/http";
import { API } from "~/constants/endpoints";
import { QUERY_KEYS, CACHE_TIMES } from "~/lib/cache-config";
import { useAuthStore } from "~/stores/authStore";

/**
 * Custom hook to fetch the list of users.
 *
 * @return An object containing the query state and methods provided by `useQuery`.
 * @since 0.0.1
 */
export function useUsers() {
  return useQuery({
    queryKey: computed(() => QUERY_KEYS.members.all),
    queryFn: async () => {
      const response = await http.get<ResponseList<User>>(API.PATH.USERS);
      return response.data;
    },
    staleTime: CACHE_TIMES.STATIC_DATA, // 30 minutos - lista de usuários muda pouco
    gcTime: CACHE_TIMES.GC_TIME, // Padrão otimizado
  });
}

/**
 * Custom hook to fetch the current user info.
 *
 * @return An object containing the query state and methods provided by `useQuery`.
 * @since 0.0.1
 */

export function useMe() {
  const { isAuthenticated } = useAuthStore();

  return useQuery<ResponseObject<User>>({
    queryKey: computed(() => [...QUERY_KEYS.members.all, "me"]),
    queryFn: async () => {
      const response = await http.get<ResponseObject<User>>(API.PATH.ME);
      return response.data;
    },
    staleTime: CACHE_TIMES.USER_DATA, // 10 minutos - dados do usuário atual
    gcTime: CACHE_TIMES.GC_TIME, // Padrão otimizado
    enabled: computed(() => isAuthenticated), // Habilitada apenas se autenticado
  });
}

export function useMeAvatar() {
  const { isAuthenticated } = useAuthStore();

  return useQuery<ResponseObject<User>>({
    queryKey: computed(() => [...QUERY_KEYS.members.all, "avatar"]),
    queryFn: async () => {
      const response = await http.get<ResponseObject<User>>(API.PATH.ME_AVATAR);
      return response.data;
    },
    staleTime: CACHE_TIMES.STATIC_DATA, // 30 minutos - avatar muda raramente
    gcTime: CACHE_TIMES.GC_TIME, // Padrão otimizado
    enabled: computed(() => isAuthenticated), // Habilitada apenas se autenticado
    retry: (failureCount, error: unknown) => {
      if (typeof error === "object" && error !== null && "response" in error) {
        const err = error as { response?: { status?: number } };
        if (err.response?.status === 404) return false;
      }
      return failureCount < 2;
    },
  });
}
